import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Observable, Subscription, catchError, forkJoin, map, of, switchMap, tap, take, combineLatest } from 'rxjs';
import { ChargeItem, DiagnosticReport, FhirResource, Observation, Patient, Reference, ServiceRequest } from 'fhir/r4';

import { backendEndPointToken } from '../../app.config';
import { AddObservationComponent } from '../../patient-observation/add-observation/add-observation.component';
import { EncounterServiceService } from '../../patient-wrapper/encounter-service.service';
import { ErrorService } from '../error.service';
import { ObservationFinalizationService } from '../observation-finalization.service';
import { StateService } from '../state.service';
import { UtilityService } from '../utility.service';
import { InfoDialogService } from '../info-dialog/info-dialog.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { query } from 'express';
import { ChipsDirective } from '../../chips.directive'
import { SuccessToastService } from '../success-toast.service';
import { ObservationReferenceRangeSuggestionDialogComponent, ReferenceRangeSuggestionDialogResult } from '../../observation-reference-ranges/reference-range-suggestion-dialog/reference-range-suggestion-dialog.component';
import { ObservationReferenceRangeMatcherService } from '../../shared/observation-reference-range-matcher.service';
import { OrganizationSettingsService } from '../organization-settings.service';
import { ObservationValueDisplayComponent } from '../observation-value-display/observation-value-display.component';

type Row = {
    key: string;
    requiredCodeableConcept: any;
    observation?: Observation & any;
};

@Component({
    selector: 'app-required-observation-results-table',
    standalone: true,
    imports: [CommonModule, ChipsDirective, MatCardModule, MatButtonModule, MatIconModule, MatMenuModule, MatDialogModule, MatCheckboxModule, ObservationValueDisplayComponent],
    templateUrl: './required-observation-results-table.component.html',
    styleUrls: ['./required-observation-results-table.component.scss']
})
export class RequiredObservationResultsTableComponent implements OnChanges, OnInit {
    private readonly http = inject(HttpClient);
    private readonly backendEndPoint = inject(backendEndPointToken);
    private readonly dialog = inject(MatDialog);
    private readonly encounterService = inject(EncounterServiceService);
    private readonly errorService = inject(ErrorService);
    private readonly finalization = inject(ObservationFinalizationService);
    private readonly stateService = inject(StateService);
    private readonly infoService = inject(InfoDialogService);
    private readonly utilityService = inject(UtilityService);
    private readonly successToast = inject(SuccessToastService);
    private readonly obsRangesMatcher = inject(ObservationReferenceRangeMatcherService);
    orgSettings = inject(OrganizationSettingsService);

    canManageManualReferenceRanges$ = this.orgSettings.canManageManualReferenceRanges();
    
    componentRef = inject(MatDialogRef<RequiredObservationResultsTableComponent>, { optional: true });

    close(): void {
        this.componentRef?.close();
    }

    private readonly dialogData = inject(MAT_DIALOG_DATA, { optional: true }) as
        | {
            serviceRequest?: ServiceRequest;
            patientId?: string;
            chargeItem?: ChargeItem | null;
            enforcePaymentForFinal?: boolean;
            paymentBalanced?: boolean;
            forSelection?: boolean;
            serviceRequestIds?: string[];
            forViewingOnly?: boolean;
            observationsToMatch?: Observation[];
        }
        | undefined;

    @Input() serviceRequest?: ServiceRequest;
    @Input() patientId?: string;
    @Input() chargeItem?: ChargeItem | null;
    @Input() enforcePaymentForFinal = false;
    @Input() paymentBalanced = false;
    @Input() serviceRequestIds: string[] = [];
    @Input() forSelection = false;
    @Input() forViewingOnly = false;
    @Input() observationsToMatch: Observation[] = [];


    @Output() selectionChange = new EventEmitter<Row[]>();
    // utilityService = inject(UtilityService);

    loading = false;
    rows: Row[] = [];
    patientContext?: Patient;

    private expectedServiceRequestIds: string[] = [];
    private readonly serviceRequestStatusById = new Map<string, string>();
    serviceRequestCompletionLoading = false;

    get editingLockedByServiceRequestCompletion(): boolean {
        if (!this.expectedServiceRequestIds.length) return false;
        for (const id of this.expectedServiceRequestIds) {
            const st = (this.serviceRequestStatusById.get(id) ?? '').toString().trim().toLowerCase();
            if (!st) return false; // unknown; do not lock
            if (st !== 'completed') return false;
        }
        return true;
    }

    private srSub?: Subscription;
    private multiSrSub?: Subscription;

    readonly selection = new SelectionModel<Row>(true, []);

    private confirmFinalize$(opts: { title: string; confirmText?: string }): Observable<boolean> {
        const ref = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '520px',
            data: {
                title: opts.title,
                message: 'Final results cannot be changed.',
                confirmText: opts.confirmText || 'Confirm',
                cancelText: 'Cancel',
                icon: 'warning'
            }
        });
        return ref.afterClosed().pipe(map((v: any) => v === true));
    }

    get selectedRows(): Row[] {
        return this.selection.selected;
    }

    ngOnInit(): void {
        this.applyDialogDataIfPresent();
        this.resolveServiceRequestCompletionState();
        this.resolvePatientContext();
        if (this.serviceRequest?.id) this.refresh();
        //IF THERE ARE SERVICERequestIds, gt all of their observations
        else if (this.serviceRequestIds && this.serviceRequestIds.length > 0) {
            // this.loading = true;
            // //  const patientRefForQuery = patientId
            // // ? (includeDomain ? `${this.backendEndPoint}/Patient/${patientId}` : `Patient/${patientId}`)
            // // : undefined;
            // const srRefsToMatch: string[] = [];
            // const querySearchArrays: { key: string; value: string }[] = [];
            // this.serviceRequestIds.forEach(id => {
            //     const ref = `ServiceRequest/${id}`;
            //     srRefsToMatch.push(ref);
            //     srRefsToMatch.push(`${this.backendEndPoint}/ServiceRequest/${id}`);
            //     querySearchArrays.push({ key: 'basedOn.reference', value: ref });
            //     querySearchArrays.push({
            //         key: 'basedOn.reference', value: `${this.backendEndPoint}/ServiceRequest/${id}`

            //     });
            // });

            // const obsRequests = this.utilityService.threeTierQueryAndSyncToStateOr$<Observation>({
            //     resourceType: 'Observation',
            //     tier3FollowNextLinks: true,
            //     urlSearchObject: {
            //         'based-on:': srRefsToMatch.join(','),

            //     },
            //     // querySearchObject: this.serviceRequestIds.map(id => ({
            //     //     key: 'basedOn',
            //     //     value: `ServiceRequest/${id}`
            //     // }))
            //     querySearchObject: querySearchArrays

            // }).pipe(
            //     tap((observations: Observation[]) => {
            //         console.log('Loaded observations for multiple SRs', this.serviceRequestIds, observations);
// $#$            //         // /* debugger; */
            //         const out = observations.map(o => ({ key: `extra:${this.codeKey(o?.code) || (o?.id ? `id:${o.id}` : '')}`, requiredCodeableConcept: o?.code, observation: o }));
            //         this.rows = out;
            //         this.syncSelectionToRows();
            //     }),
            //     catchError((err) => {
            //         console.error('Failed to load observations for multiple service requests', err);
            //         this.errorService.openandCloseError('Failed to load observations for the selected requests.');
            //         return of([] as Observation[]);
            //     }),
            //     tap(() => {
            //         this.loading = false;
            //     })
            // ).subscribe(() => {
            //     if (this.dialogData?.observationsToMatch) {
            //         this.observationsToMatch = [...this.dialogData.observationsToMatch];
            //         this.rows.forEach(r => {
            //             const match = this.observationsToMatch.find(o => o.id === r.observation?.id);
            //             if (match) this.selection.select(r);
            //         });

            //     }
            // });
            this.loadObservationsForServiceRequestIds();


        }

    }

    ngOnChanges(): void {
        this.applyDialogDataIfPresent();
        this.resolveServiceRequestCompletionState();
        this.resolvePatientContext();
        if (!this.serviceRequest?.id) return;
        this.refresh();
    }

    private resolveServiceRequestCompletionState(): void {
        const ids: string[] = [];

        if (this.serviceRequest?.id) {
            ids.push(this.serviceRequest.id);
            const st = (this.serviceRequest as any)?.status;
            if (st) this.serviceRequestStatusById.set(this.serviceRequest.id, st);
        }

        for (const id of (this.serviceRequestIds ?? [])) {
            const s = (id ?? '').toString().trim();
            if (s) ids.push(s);
        }

        this.expectedServiceRequestIds = Array.from(new Set(ids));
        if (!this.expectedServiceRequestIds.length) return;

        // Best-effort: use state cache first.
        try {
            const inState = (this.stateService.orgWideResources.serviceRequests.getValue() ?? [])
                .map((x: any) => x?.actualResource)
                .filter(Boolean) as any[];
            for (const sr of inState) {
                const id = (sr?.id ?? '').toString().trim();
                if (id && this.expectedServiceRequestIds.includes(id) && !this.serviceRequestStatusById.has(id)) {
                    const st = (sr?.status ?? '').toString();
                    if (st) this.serviceRequestStatusById.set(id, st);
                }
            }
        } catch {
            // ignore
        }

        const missing = this.expectedServiceRequestIds.filter((id) => !this.serviceRequestStatusById.has(id));
        if (!missing.length) return;

        this.serviceRequestCompletionLoading = true;
        forkJoin(
            missing.map((id) =>
                this.http.get<ServiceRequest>(`${this.backendEndPoint}/ServiceRequest/${encodeURIComponent(id)}`).pipe(
                    catchError(() => of(null as any))
                )
            )
        ).subscribe((list: Array<ServiceRequest | null>) => {
            this.serviceRequestCompletionLoading = false;
            for (const sr of (list ?? []).filter(Boolean) as ServiceRequest[]) {
                const id = (sr?.id ?? '').toString().trim();
                if (!id) continue;
                const st = (sr as any)?.status;
                if (st) this.serviceRequestStatusById.set(id, st);
            }
        });
    }

    private applyDialogDataIfPresent(): void {
        const d = this.dialogData;
        if (!d) return;
        if (!this.serviceRequest && d.serviceRequest) this.serviceRequest = d.serviceRequest as any;
        if (this.patientId == null && d.patientId) this.patientId = d.patientId;
        if (this.chargeItem === undefined && d.chargeItem !== undefined) this.chargeItem = d.chargeItem;
        if (!this.enforcePaymentForFinal && d.enforcePaymentForFinal === true) this.enforcePaymentForFinal = true;
        if (!this.paymentBalanced && d.paymentBalanced === true) this.paymentBalanced = true;

        if (!this.forViewingOnly && d.forViewingOnly === true) this.forViewingOnly = true;

        if (!this.forSelection && d.forSelection === true) this.forSelection = true;
        if ((!this.serviceRequestIds || this.serviceRequestIds.length === 0) && Array.isArray(d.serviceRequestIds)) {
            this.serviceRequestIds = d.serviceRequestIds;
        }
    }

    private resolvePatientContext(): void {
        const explicitId =
            this.patientId ||
            this.dialogData?.patientId ||
            this.extractIdFromReference(this.serviceRequest?.subject?.reference, 'Patient') ||
            undefined;

        const current = this.stateService.PatientResources.currentPatient.getValue()?.actualResource;
        if (current?.id && (!explicitId || current.id === explicitId)) {
            this.patientContext = current;
            return;
        }

        if (!explicitId) {
            this.patientContext = current;
            return;
        }

        const orgWideMatch = this.stateService.orgWideResources.patient
            .getValue()
            ?.find(p => p?.actualResource?.id === explicitId)?.actualResource;
        if (orgWideMatch) {
            this.patientContext = orgWideMatch;
            return;
        }

        this.http
            .get<Patient>(`${this.backendEndPoint}/Patient/${encodeURIComponent(explicitId)}`)
            .pipe(take(1))
            .subscribe({
                next: patient => (this.patientContext = patient),
                error: () => {
                    // best-effort lookup; silent failure is acceptable in dialog context
                }
            });
    }

    private ensureEditable(): boolean {
        if (this.forViewingOnly) {
            this.infoService.info('Viewing only: editing is disabled.');
            return false;
        }

        if (this.editingLockedByServiceRequestCompletion) {
            this.infoService.info('This ServiceRequest is completed. Editing actions are disabled.');
            return false;
        }
        return true;
    }

    get hasServiceRequest(): boolean {
        return !!this.serviceRequest?.id;
    }

    private showMissingServiceRequestInfo(): void {
        this.infoService.info(
            'Cannot confirm results. This component is probably opened through the diagnostic report viewer, and the associated Service Request is not known. Please finalize results one by one through the add result button.'
        );
    }

    onAddObservationClick(): void {
        if (!this.ensureEditable()) return;
        if (!this.hasServiceRequest) {
            this.showMissingServiceRequestInfo();
            return;
        }
        this.addMoreObservation();
    }

    onRowActionsClick(row: Row, ev?: Event): void {
        if (!this.hasServiceRequest) {
            ev?.preventDefault?.();
            ev?.stopPropagation?.();
            this.showMissingServiceRequestInfo();
            return;
        }
        if (!row?.observation) {
            ev?.preventDefault?.();
            ev?.stopPropagation?.();
        }
    }

    refresh(): void {
        if (this.serviceRequest?.id) {
            this.srSub?.unsubscribe();
            const sr = this.serviceRequest!;
            const srRef = `ServiceRequest/${sr.id}`;

            const srCode = this.getServiceRequestCode(sr);

            this.loading = true;

            const srSubjectReferenceStringSplitLength: number = sr.subject?.reference?.split('/').length || 0;
            let patientIdFromServiceRequest: string | undefined = undefined;
            if (srSubjectReferenceStringSplitLength > 2) {
                patientIdFromServiceRequest = sr.subject!.reference!.split('/')[srSubjectReferenceStringSplitLength - 1];

            }

            let requiredCodes$: Observable<any[]> = of([]);
            if (srCode) {
                requiredCodes$ = this.encounterService.getConfiguredRequiredObservationCodes$(srCode);
                
            }

            this.srSub = combineLatest({
                requiredCodes: requiredCodes$,
                observations: this.listObservationsForServiceRequest(sr, patientIdFromServiceRequest ?? undefined)
            })
                .pipe(
                    map(({ requiredCodes, observations }) => {
                        
                        console.log('Loaded observations for SR', requiredCodes, observations);
                        const configured = Array.isArray(requiredCodes) ? requiredCodes : [];

                        // If there are no configured codes, fall back to existing observations list.
                        // if (!configured.length) {
                        const unique = new Map<string, any>();
                        for (const o of observations) {
                            console.log('Processing observation', o);
                            const key = this.codeKey(o?.code) || (o?.id ? `id:${o.id}` : crypto.randomUUID?.() || String(Math.random()));
                            if (!unique.has(key)) unique.set(key, o?.code);
                        }
                        return {
                            configuredCodes: configured.length ? configured : [],
                            observations
                        };
                        // }

                        return { configuredCodes: configured, observations };
                    }),
                    map(({ configuredCodes, observations }) => {
                        const out: Row[] = [];

                        console.log(configuredCodes, observations);

                        for (const cc of configuredCodes) {
                            const key = this.codeKey(cc) || (crypto.randomUUID?.() || String(Math.random()));
                            const match = observations.find((o) => this.codesMatch(o?.code, cc) && o.status !== 'registered');
                            out.push({ key, requiredCodeableConcept: cc, observation: match });
                        }

                        // Also show any extra observations under the SR that are not part of configured list.
                        for (const o of observations) {
                            const k = this.codeKey(o?.code) || (o?.id ? `id:${o.id}` : '');
                            const alreadyShown = out.some((r) => r.observation?.id && r.observation.id === (o as any)?.id);
                            if (!alreadyShown && k && o.status !== 'registered') {
                                out.push({ key: `extra:${k}`, requiredCodeableConcept: o?.code, observation: o });
                            }
                        }
                        // /filter out observation that re used for placeholder - hey have reg status
                        console.log(out);
// $#$                        // /* debugger; */
                        return out;
                    }),
                    tap((rows) => {
                        this.rows = rows;
                        this.syncSelectionToRows();
                        this.loading = false;
                    }),
                    catchError((err) => {
                        console.error('Failed to refresh required observations', err);
                        this.loading = false;
                        this.errorService.openandCloseError('Failed to load observations for this request.');
                        return of([] as Row[]);
                    })
                )
                .subscribe();
        } else if (this.serviceRequestIds && this.serviceRequestIds.length > 0) {
            this.loadObservationsForServiceRequestIds();
        }
    }

    private loadObservationsForServiceRequestIds(): void {
        if (!this.serviceRequestIds || this.serviceRequestIds.length === 0) return;

        this.multiSrSub?.unsubscribe();

        this.loading = true;

        const srRefsToMatch: string[] = [];
        this.serviceRequestIds.forEach(id => {
            srRefsToMatch.push(`ServiceRequest/${id}`);
            srRefsToMatch.push(`${this.backendEndPoint}/ServiceRequest/${id}`);
        });

        // Important: `querySearchObject` is ANDed across entries in `applyLocalFilters`.
        // To get OR behavior for multiple SR refs, provide a single filter with an array value.
        // Use the *streaming* variant so state updates (e.g., after PUT) can re-render without relying on a refetch.
        this.multiSrSub = this.utilityService.threeTierQueryAndSyncToStateOr$<Observation>({
            resourceType: 'Observation',
            tier3FollowNextLinks: true,
            scope: 'orgWide',
            urlSearchObject: {
                'based-on:': srRefsToMatch.join(','),
            },
            querySearchObject: [{ key: 'basedOn.reference', value: srRefsToMatch }]
        }).pipe(
            map((observations) => (Array.isArray(observations) ? observations : []).filter(o => o?.status !== 'registered')),
            tap((observations: Observation[]) => {
                console.log('Loaded observations for multiple SRs', this.serviceRequestIds, observations);

                const out = observations.map(o => ({
                    key: `extra:${this.codeKey(o?.code) || (o?.id ? `id:${o.id}` : '')}`,
                    requiredCodeableConcept: o?.code,
                    observation: o
                }));

                this.rows = out;
                this.syncSelectionToRows();

                if (this.dialogData?.observationsToMatch) {
                    this.observationsToMatch = [...this.dialogData.observationsToMatch];
                }

                if (Array.isArray(this.observationsToMatch) && this.observationsToMatch.length) {
                    this.rows.forEach(r => {
                        const match = this.observationsToMatch.find(o => o.id === r.observation?.id);
                        if (match) this.selection.select(r);
                    });
                }
            }),
            catchError((err) => {
                console.error('Failed to load observations for multiple service requests', err);
                this.errorService.openandCloseError('Failed to load observations for the selected requests.');
                return of([] as Observation[]);
            }),
            tap(() => {
                this.loading = false;
            })
        ).subscribe();
    }

    private persistUpdatedObservationToState(updated: Observation): void {
        // This component queries org-wide state for Observations.
        // Ensure updates reflect in both patient-scoped and org-wide caches.
        // this.stateService.persistLocalResource(updated as any, 'saved');
        this.stateService.persistOrgWideResource(updated as any, 'saved');
    }

    addToSelection(row: Row): void {
        if (this.forViewingOnly) return;
        this.selection.select(row);
        this.emitSelection();
    }

    removeFromSelection(row: Row): void {
        if (this.forViewingOnly) return;
        this.selection.deselect(row);
        this.emitSelection();
    }

    toggleSelection(row: Row): void {
        if (this.forViewingOnly) return;
        this.selection.toggle(row);
        this.emitSelection();
    }

    setSelected(row: Row, selected: boolean): void {
        if (this.forViewingOnly) return;
        if (selected) this.selection.select(row);
        else this.selection.deselect(row);
        this.emitSelection();
    }

    selectAll(): void {
        if (this.forViewingOnly) return;
        if (!this.rows.length) return;
        this.selection.select(...this.rows);
        this.emitSelection();
    }

    clearSelection(): void {
        if (this.forViewingOnly) return;
        if (!this.selection.hasValue()) return;
        this.selection.clear();
        this.emitSelection();
    }

    setAllSelected(selected: boolean): void {
        if (this.forViewingOnly) return;
        if (selected) this.selectAll();
        else this.clearSelection();
    }

    isAllSelected(): boolean {
        return this.rows.length > 0 && this.selection.selected.length === this.rows.length;
    }

    checkboxLabel(row?: Row): string {
        if (!row) return this.isAllSelected() ? 'deselect all' : 'select all';
        return this.selection.isSelected(row) ? 'deselect row' : 'select row';
    }

    private emitSelection(): void {
        this.selectionChange.emit(this.selection.selected);
    }

    private syncSelectionToRows(): void {
        if (!this.selection.hasValue()) return;
        const selectedKeys = new Set(this.selection.selected.map((r) => r.key));
        this.selection.clear();
        for (const row of this.rows) {
            if (selectedKeys.has(row.key)) this.selection.select(row);
        }
        this.emitSelection();
    }

    openReferenceRangeDialog(row: Row): void {
        if (!row?.observation?.id) {
            this.errorService.openandCloseError('Save the observation before adding reference ranges.');
            return;
        }
        if (!this.ensureEditable()) {
            return;
        }
        const dialogRef = this.dialog.open(ObservationReferenceRangeSuggestionDialogComponent, {
            width: '820px',
            maxHeight: '92vh',
            data: {
                observation: row.observation,
                patient: this.patientContext
            }
        });

        dialogRef.afterClosed().subscribe((result?: ReferenceRangeSuggestionDialogResult) => {
            if (result?.updatedObservation) {
                this.persistUpdatedObservationToState(result.updatedObservation);
                this.successToast.success('Reference range applied.');
                this.refresh();
            }
        });
    }

    openResultDialog(row: Row): void {
        if (!this.ensureEditable()) return;
        //can no edit final obs

        //  

        if (this.serviceRequest && this.serviceRequest?.id) {

            if (row.observation?.status === 'final') {
                this.errorService.openandCloseError('Cannot edit a finalized observation., try cancelling and then add a new one.',);
                return;
            }
            //can also not dit  a cancelled obs
            if (row.observation?.status === 'cancelled') {
                this.errorService.openandCloseError('Cannot edit a cancelled observation., try adding a new one.',);
                return;
            }
            const sr = this.serviceRequest;

            // If the placeholder observation is missing, create a stub so AddObservationComponent
            // can prefill the test code and POST a new Observation.
            const stubPatientId = this.patientId || this.extractIdFromReference(sr.subject?.reference, 'Patient') || undefined;
            const existingOrStub: Observation = row.observation
                ? row.observation
                : {
                    resourceType: 'Observation',
                    status: 'preliminary',
                    code: row.requiredCodeableConcept,
                    basedOn: [{ reference: `ServiceRequest/${sr.id}` }],
                    ...(stubPatientId ? { subject: { reference: `Patient/${stubPatientId}` } } : {}),
                    ...(sr.encounter ? { encounter: sr.encounter as any } : {})
                };

            const ref = this.dialog.open(AddObservationComponent, {
                width: '900px',
                maxHeight: '90vh',
                autoFocus: false,
                data: {
                    isAnyCategory: false,
                    observationCategoryValue: 'laboratory',
                    patientId: this.patientId,
                    serviceRequest: sr,
                    existingObservation: existingOrStub,
                    chargeItem: this.chargeItem ?? null,
                    enforcePaymentForFinal: this.enforcePaymentForFinal,
                    paymentBalanced: this.paymentBalanced,
                    forceStatusValue: 'preliminary',
                    hideLabStatusField: true
                }
            });

            ref.afterClosed().subscribe((saved: Observation | undefined) => {
                if (!saved) return;
                // if ((saved as any)?.resourceType === 'Observation' && (saved as any)?.id) {
                this.persistUpdatedObservationToState(saved as any);
                // } else {
                //     this.stateService.persistLocalResource(saved as any, (saved as any)?.id ? 'saved' : 'unsaved');
                // }
                this.refresh();
            });
        }
        //show the info dialog
        else {
            this.showMissingServiceRequestInfo();
        }
    }

    confirm(row: Row): void {
        if (!this.ensureEditable()) return;
        if (this.enforcePaymentForFinal && !this.paymentBalanced) {
            this.errorService.openandCloseError('Payment has not been confirmed (Invoice must be balanced) to finalize results.');
            return;
        }
        const obs: any = row.observation;
        if (!obs?.id) {
            this.errorService.openandCloseError('No saved observation to confirm yet. Enter a result first.');
            return;
        }

        //cannt confirm a final obs || cancelled obs
        if (obs?.status === 'final') {
            this.errorService.openandCloseError('Observation is already finalized.');
            return;
        }
        if (obs?.status === 'cancelled') {
            this.errorService.openandCloseError('Cannot confirm a cancelled observation.');
            return;
        }
        // also, check that the observation has a value (either value[x] or component with value[x]) before allowing confirmation
        const hasValue = Object.keys(obs).some((k) => k.startsWith('value') && obs[k] != null) || (Array.isArray(obs.component) && obs.component.some((c: any) => Object.keys(c).some((k) => k.startsWith('value') && c[k] != null)));
        if (!hasValue) {
            this.errorService.openandCloseError('Observation has no value. Enter a result first.');
            return;
        }

        //confirmation dialog before continuing

        const next: any = { ...obs, status: 'final' };
        const srRef = this.serviceRequest?.id ? `ServiceRequest/${this.serviceRequest.id}` : row.observation?.basedOn?.find((b: Reference) =>
            String(b.reference || '').includes('ServiceRequest/'))?.reference || null;

        this.confirmFinalize$({ title: 'Confirm result' })
            .pipe(
                switchMap((confirmed) => confirmed
                    ? this.finalization.validateSingleFinalization$({ serviceRequestRef: srRef, observationToSave: next })
                    : of(false)),
                switchMap((ok) => (ok ? this.http.put(`${this.backendEndPoint}/Observation/${obs.id}`, next, {
                    headers: {
                        'Content-Type': 'application/fhir+json',
                        Accept: 'application/fhir+json',
                        Prefer: 'return=representation'
                    }
                }) : of(null))),
                catchError((err) => {
                    console.error('Failed to confirm observation', err);
                    this.errorService.openandCloseError('Failed to confirm result.');
                    return of(null);
                })
            )
            .subscribe((resp: any) => {
                if (!resp) return;
                this.persistUpdatedObservationToState(resp as any);
                this.successToast.success('Result confirmed successfully.');
                this.refresh();
            });
    }

    cancel(row: Row): void {
        //If already cancelled, do nothing
        if (row.observation?.status === 'cancelled') {
            this.errorService.openandCloseError('Observation is already cancelled.');
            return;
        }

        if (!this.ensureEditable()) return;
        const obs: any = row.observation;
        if (!obs?.id) {
            this.errorService.openandCloseError('No saved observation to cancel.');
            return;
        }

        // if (!this.stateService.isEncounterActive()) {
        //     this.errorService.openandCloseError('Cannot cancel result: encounter is not in progress.');
        //     return;
        // }

        const doCancel$ = () =>
            this.http.put(
                `${this.backendEndPoint}/Observation/${obs.id}`,
                {
                    ...obs,
                    status: 'cancelled'
                },
                {
                    headers: {
                        'Content-Type': 'application/fhir+json',
                        Accept: 'application/fhir+json',
                        Prefer: 'return=representation'
                    }
                }
            );

        // For non-final results: cancel immediately (no DiagnosticReport guard needed).
        if (obs.status !== 'final') {
            doCancel$()
                .pipe(
                    catchError((err) => {
                        console.error('Failed to cancel observation', err);
                        this.errorService.openandCloseError('Failed to cancel result.');
                        return of(null);
                    })
                )
                .subscribe((resp: any) => {
                    if (!resp) return;
                    const updated = resp && resp.resourceType !== 'OperationOutcome' ? resp : null;
                    if (!updated) return;
                    this.persistUpdatedObservationToState(updated as any);
                    this.successToast.success('Result cancelled successfully.');
                    this.refresh();
                });
            return;
        }

        // For final results: only block if a DiagnosticReport already exists for the ServiceRequest.
        const srRef = this.resolveServiceRequestReference(obs);
        if (!srRef) {
            this.infoService.info('Cannot cancel a finalized result because the associated Service Request is not known.');
            return;
        }

        const patientId = this.patientId || this.extractIdFromReference(this.serviceRequest?.subject?.reference, 'Patient') || undefined;

        this.ensureNoDiagnosticReportForServiceRequest$(srRef, patientId)
            .pipe(
                switchMap((ok) => (ok ? doCancel$() : of(null))),
                catchError((err) => {
                    console.error('Failed to cancel observation', err);
                    this.errorService.openandCloseError('Failed to cancel result.');
                    return of(null);
                })
            )
            .subscribe((resp: any) => {
                if (!resp) return;
                const updated = resp && resp.resourceType !== 'OperationOutcome' ? resp : null;
                if (!updated) return;
                this.persistUpdatedObservationToState(updated as any);
                this.successToast.success('Result cancelled successfully.');
                this.refresh();
            });
    }
    confirmAll(): void {
        if (!this.ensureEditable()) return;
        if (!this.serviceRequest?.id) {
            //an info dialog
            this.infoService.info('Cannot confirm results This component is probaby opened through the diagnostic report viewer, and the associated Service Request is not known. Please finalize results one by one through the add result button.');
            return;
        }
        if (this.enforcePaymentForFinal && !this.paymentBalanced) {
            this.errorService.openandCloseError('Payment has not been confirmed (Invoice must be balanced) to finalize results.');
            return;
        }
        const srRef = this.serviceRequest?.id ? `ServiceRequest/${this.serviceRequest.id}` : null;
        const targets = this.rows
            .map((r) => r.observation)
            .filter((o: any) => !!o?.id).filter((o: any) => o?.status !== 'final' && o?.status !== 'cancelled')
            .map((o: any) => ({ ...o, status: 'final' }));

        if (!targets.length) {
            this.errorService.openandCloseError('No saved observations to confirm.');
            return;
        }

        this.confirmFinalize$({ title: 'Confirm all results', confirmText: 'Confirm all' })
            .pipe(
                switchMap((confirmed) => confirmed
                    ? this.finalization.validateBatchFinalization$({ serviceRequestRef: srRef, observationsToSave: targets })
                    : of(false)),
                switchMap((ok) => {
                    if (!ok) return of([]);
                    const saves = targets.map((o: any) =>
                        this.http.put(`${this.backendEndPoint}/Observation/${o.id}`, o, {
                            headers: {
                                'Content-Type': 'application/fhir+json',
                                Accept: 'application/fhir+json',
                                Prefer: 'return=representation'
                            }
                        })
                    );
                    return forkJoin(saves).pipe(catchError(() => of([])));
                })
            )
            .subscribe((respList: any[]) => {
                (respList || []).forEach((resp: any, idx: number) => {
                    const saved = resp && resp.resourceType !== 'OperationOutcome' ? resp : targets[idx];
                    this.persistUpdatedObservationToState(saved as any);
                });
                this.successToast.success(`Confirmed ${targets.length} result${targets.length === 1 ? '' : 's'} successfully.`);
                this.refresh();
            });
    }

    cancelAll(): void {
        if (!this.ensureEditable()) return;

        // if (!this.stateService.isEncounterActive()) {
        //     this.errorService.openandCloseError('Cannot cancel results: encounter is not in progress.');
        //     return;
        // }

        const targets = this.rows
            .map((r) => r.observation)
            .filter((o: any) => !!o?.id).filter((o: any) => o?.status !== 'cancelled');

        if (!targets.length) {
            this.errorService.openandCloseError('No saved observations to cancel.');
            return;
        }

        const finalTargets = targets.filter((o: any) => o?.status === 'final');
        const nonFinalTargets = targets.filter((o: any) => o?.status !== 'final');

        const cancelTargets = (list: any[]) => {
            const patches = list.map((o: any) =>
                this.http.put(
                    `${this.backendEndPoint}/Observation/${o.id}`,
                    {
                        ...o,
                        status: 'cancelled'
                    },
                    {
                        headers: {
                            'Content-Type': 'application/fhir+json',
                            Accept: 'application/fhir+json',
                            Prefer: 'return=representation'
                        }
                    }
                )
            );

            forkJoin(patches)
                .pipe(catchError(() => of([])))
                .subscribe((respList: any[]) => {
                    (respList || []).forEach((resp: any, idx: number) => {
                        const fallback = { ...list[idx], status: 'cancelled' };
                        const saved = resp && resp.resourceType !== 'OperationOutcome' ? resp : fallback;
                        this.persistUpdatedObservationToState(saved as any);
                    });
                    this.successToast.success(`Cancelled ${list.length} result${list.length === 1 ? '' : 's'} successfully.`);
                    this.refresh();
                });
        };

        // Non-final results: cancel immediately.
        if (!finalTargets.length) {
            cancelTargets(nonFinalTargets);
            return;
        }

        // Final results: only cancel them if there is no DiagnosticReport for the SR.
        const srRef = this.resolveServiceRequestReference(finalTargets[0]);
        if (!srRef) {
            this.infoService.info('Some results are finalized but the Service Request is not known, so finalized results were skipped.');
            if (nonFinalTargets.length) cancelTargets(nonFinalTargets);
            else this.errorService.openandCloseError('No cancellable observations found.');
            return;
        }

        const patientId = this.patientId || this.extractIdFromReference(this.serviceRequest?.subject?.reference, 'Patient') || undefined;

        this.ensureNoDiagnosticReportForServiceRequest$(srRef, patientId)
            .pipe(map((ok) => (ok ? [...nonFinalTargets, ...finalTargets] : nonFinalTargets)))
            .subscribe((listToCancel: any[]) => {
                if (!listToCancel.length) {
                    this.errorService.openandCloseError('No cancellable observations found., All final results have already been referenced elsewhere.');
                    return;
                }
                cancelTargets(listToCancel);
            });
    }

    addMoreObservation(): void {
        if (!this.ensureEditable()) return;
        const sr = this.serviceRequest;
        if (!sr) return;

        const ref = this.dialog.open(AddObservationComponent, {
            width: '900px',
            maxHeight: '90vh',
            autoFocus: false,
            data: {
                // isAnyCategory: true,
                observationCategoryValue: sr?.category?.[0]?.coding?.[0]?.code || 'laboratory',
                patientId: this.patientId,
                serviceRequest: sr,
                chargeItem: this.chargeItem ?? null,
                enforcePaymentForFinal: this.enforcePaymentForFinal,
                paymentBalanced: this.paymentBalanced,
                forceStatusValue: 'preliminary',
                hideLabStatusField: true
            }
        });

        ref.afterClosed().subscribe((saved: Observation | undefined) => {
            if (!saved) return;
            // if ((saved as any)?.resourceType === 'Observation' && (saved as any)?.id) {
            this.persistUpdatedObservationToState(saved as any);
            // } else {
            //     this.stateService.persistLocalResource(saved as any, (saved as any)?.id ? 'saved' : 'unsaved');
            // }
            this.refresh();
        });
    }

    formatCodeDisplay(cc: any): string {
        const coding = Array.isArray(cc?.coding) ? cc.coding : [];
        const first = coding[0];
        const display = (first?.display || cc?.text || '').toString().trim();
        const code = (first?.code || '').toString().trim();
        if (display && code) return `${display} (${code})`;
        return display || code || '—';
    }

    formatStatus(obs?: any): string {
        return obs?.status ? String(obs.status).toUpperCase() : '—';
    }
    formatValue(obs?: any): string {
        if (!obs) return '—';
        return this.utilityService.convertAllValueTypesToString(obs)
    }

    private extractIdFromReference(ref?: string | null, resourceType?: string): string | null {
        const raw = (ref || '').toString().trim();
        if (!raw) return null;
        const parts = raw.split('/').filter(Boolean);
        if (!parts.length) return null;
        if (resourceType) {
            const idx = parts.findIndex((p) => p === resourceType);
            if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
        }
        return parts[parts.length - 1] || null;
    }

    private resolveServiceRequestReference(observationOrRowObs: any): string | null {
        if (this.serviceRequest?.id) return `ServiceRequest/${this.serviceRequest.id}`;
        const basedOn = Array.isArray(observationOrRowObs?.basedOn) ? observationOrRowObs.basedOn : [];
        const sr = basedOn.find((b: Reference) => String(b?.reference || '').includes('ServiceRequest/'));
        return sr?.reference ? String(sr.reference) : null;
    }

    private ensureNoDiagnosticReportForServiceRequest$(srRef: string, patientId?: string): Observable<boolean> {
        const includeDomain = !!this.serviceRequest?.subject?.reference?.includes(this.backendEndPoint);
        const srId = this.extractIdFromReference(srRef, 'ServiceRequest');
        const srRefForQuery = includeDomain && srId ? `${this.backendEndPoint}/ServiceRequest/${srId}` : srRef;
        const patientRefForQuery = patientId
            ? (includeDomain ? `${this.backendEndPoint}/Patient/${patientId}` : `Patient/${patientId}`)
            : undefined;

        return this.utilityService.threeTierQueryAndSyncToStateOnce$<DiagnosticReport>({
            resourceType: 'DiagnosticReport',
            urlSearchObject: {
                'based-on': srRefForQuery,
                ...(patientRefForQuery ? { subject: patientRefForQuery } : {})
            },
            useCurrentPatientId: false,
            querySearchObject: [
                { key: 'basedOn.reference', value: srRefForQuery },
                ...(patientRefForQuery ? [{ key: 'subject.reference', value: patientRefForQuery }] : [])
            ]
        }).pipe(
            map((reports) => Array.isArray(reports) ? reports : []),
            map((reports) => reports.length === 0),
            tap((ok) => {
                if (!ok) {
                    this.errorService.openandCloseError('Cannot cancel result: a Diagnostic Report already exists for this request.');
                }
            }),
            catchError((err) => {
                console.error('Failed to check DiagnosticReport for cancellation guard', err);
                this.errorService.openandCloseError('Could not verify report status. Please try again.');
                return of(false);
            })
        );
    }

    private listObservationsForServiceRequest(sr: ServiceRequest, patientId?: string) {
        // Prefer patient-scoped state if already loaded.
        // const fromState = (this.stateService.PatientResources?.observations?.getValue?.() || [])
        //     .map((w: any) => w?.actualResource)
        //     .filter(Boolean)
        //     .filter((o: Observation) => Array.isArray(o?.basedOn) && o.basedOn.some((b: any) => b?.reference === srRef));

        // if (fromState.length) return of(fromState as Observation[]);

        // return this.http
        //     .get<any>(`${this.backendEndPoint}/Observation?_count=200`)
        //     .pipe(
        //         tap(e => this.patientId ? this.stateService.processBundleTransaction(e) : this.stateService.processOrgWideBundleTransaction(e)),
        //         map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter((o: any) => {
        //             return Array.isArray(o?.basedOn) && o.basedOn.some((b: any) => b?.reference === srRef)
        //         }).filter(Boolean) as Observation[]),
        //         catchError(() => of([] as Observation[]))

        //     );

        const isThisServerReferenceIncludeDomain = (): boolean => {
            return sr.subject?.reference?.includes(this.backendEndPoint) || false

        }

        return this.utilityService.threeTierQueryAndSyncToState$<Observation>({
            resourceType: 'Observation',
            urlSearchObject: {
                'based-on': isThisServerReferenceIncludeDomain() ? `${this.backendEndPoint}/ServiceRequest/${sr.id}` : `ServiceRequest/${sr.id}`,
                ...(patientId ? { subject: isThisServerReferenceIncludeDomain() ? `${this.backendEndPoint}/Patient/${patientId}` : `Patient/${patientId}` } : {})

            },
            useCurrentPatientId: false,
            scope: 'orgWide',

            querySearchObject: [
                {
                    key: 'basedOn.reference',
                    value: isThisServerReferenceIncludeDomain() ? `${this.backendEndPoint}/ServiceRequest/${sr.id}` : `ServiceRequest/${sr.id}`,

                },
                ...(patientId ? [{ key: 'subject.reference', value: isThisServerReferenceIncludeDomain() ? `${this.backendEndPoint}/Patient/${patientId}` : `Patient/${patientId}` }] : [])
            ]


        })
    }

    private getServiceRequestCode(sr: ServiceRequest): string | null {
        const codings = Array.isArray(sr?.code?.coding) ? sr.code.coding : [];
        const c = codings[0];
        if (c?.code) return String(c.code);
        return null;
    }

    private codeKey(cc: any): string | null {
        if (!cc) return null;
        const coding = Array.isArray(cc?.coding) ? cc.coding : [];
        const first = coding[0];
        const code = (first?.code || '').toString().trim();
        if (!code) return null;
        const system = (first?.system || '').toString().trim();
        return `${system}|${code}`;
    }

    private normalizeTokens(cc: any): Set<string> {
        const tokens = new Set<string>();
        const coding = Array.isArray(cc?.coding) ? cc.coding : [];
        for (const c of coding) {
            const code = (c?.code || '').toString().trim();
            if (!code) continue;
            const system = (c?.system || '').toString().trim();
            tokens.add(`${system}|${code}`);
            tokens.add(`|${code}`);
        }
        const text = (cc?.text || '').toString().trim().toLowerCase();
        if (text) tokens.add(`text|${text}`);
        return tokens;
    }

    private codesMatch(a: any, b: any): boolean {
        if (!a || !b) return false;
        const at = this.normalizeTokens(a);
        const bt = this.normalizeTokens(b);
        for (const t of at) {
            if (bt.has(t)) return true;
        }
        return false;
    }
}
